import { STRIPE_CONFIG } from '../../config/stripe';

const API_BASE = STRIPE_CONFIG.apiBaseUrl || '/api';

export function toLocalDatetimeValue(isoStr, timeZone) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (!timeZone) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false
  }).formatToParts(d);
  const get = (type) => parts.find(p => p.type === type)?.value || '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

export function formatDisplayDate(datetimeStr, timeZone) {
  if (!datetimeStr) return '';
  const d = new Date(datetimeStr);
  const opts = {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  };
  if (timeZone) opts.timeZone = timeZone;
  return d.toLocaleString(undefined, opts);
}

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * Normalise a day's opening hours into an array of {open, close} windows.
 *
 *   null / undefined                   -> []
 *   { open, close }                    -> [that]
 *   [{ open, close }, { open, close }] -> filtered array (split days, lunch breaks)
 *
 * Browser-side twin of normalizeWindows in api/_utils/openingHoursCheck.js — the
 * frontend can't import from api/, so the two are parallel copies. Keep them in
 * sync, or the client will accept what the server rejects. No location currently
 * stores the array form (checked 2026-08-14); this is preventive.
 */
function normalizeWindows(dayHours) {
  if (!dayHours) return [];
  if (Array.isArray(dayHours)) return dayHours.filter((w) => w && w.open && w.close);
  if (dayHours.open && dayHours.close) return [dayHours];
  return [];
}

const parseTimeToHours = (timeStr) => {
  const [h, m] = String(timeStr).split(':').map(Number);
  return h + (m || 0) / 60;
};

export function validateDateAgainstOpeningHours(dateStr, openingHours, timeZone) {
  if (!dateStr || !openingHours) return null;

  // Wall-clock strings from <input type="datetime-local"> have no timezone
  // info and already represent merchant-local time, so parsing them through
  // `new Date()` would interpret them as browser-local time and produce a
  // wrong hour after Intl.DateTimeFormat re-converts to the merchant tz.
  const hasTzInfo = /Z$|[+-]\d{2}:?\d{2}$/.test(dateStr);

  let dayName;
  let timeInHours;

  if (!hasTzInfo) {
    const [datePart, timePart = '00:00'] = dateStr.split('T');
    const [y, m, d] = datePart.split('-').map(Number);
    const [h, min] = timePart.split(':').map(Number);
    if (!y || !m || !d) return null;
    dayName = DAY_NAMES[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
    timeInHours = h + (min || 0) / 60;
  } else {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || undefined,
      weekday: 'long', hour: 'numeric', minute: 'numeric', hour12: false
    }).formatToParts(d);
    dayName = (parts.find(p => p.type === 'weekday')?.value || '').toLowerCase();
    const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0');
    const minute = parseInt(parts.find(p => p.type === 'minute')?.value || '0');
    timeInHours = hour + minute / 60;
  }

  const windows = normalizeWindows(openingHours[dayName]);
  if (windows.length === 0) {
    return { type: 'closed', dayName };
  }

  // Bounds are inclusive at both ends, matching isMomentWithinOpeningHours.
  const withinWindow = windows.some((w) =>
    timeInHours >= parseTimeToHours(w.open) && timeInHours <= parseTimeToHours(w.close)
  );

  if (!withinWindow) {
    // `open`/`close` describe the day's overall span so the existing single-range
    // error message still reads correctly; `windows` carries the full detail for
    // callers that want to render split days precisely.
    return {
      type: 'outside',
      open: windows[0].open,
      close: windows[windows.length - 1].close,
      windows: windows.map((w) => `${w.open}-${w.close}`).join(', ')
    };
  }
  return null;
}

const HAS_TZ_INFO = /Z$|[+-]\d{2}:?\d{2}$/;

/**
 * Reduce a booking moment to the merchant-local YYYY-MM-DD it falls on.
 *
 * closing_periods rows are bare calendar dates in merchant-local terms, so the
 * booking's moments must be reduced to local dates before comparing. Deriving
 * them from toISOString() (i.e. UTC) was off by a day whenever the instant fell
 * on a different local date — for a UTC+2 merchant, any time from 22:00 UTC
 * onward. That failed OPEN: a return at 23:30Z on 15 Aug is 01:30 local on
 * 16 Aug, so a closing period covering the 16th went undetected.
 *
 * Two input shapes, same rule as validateDateAgainstOpeningHours:
 *   - no offset (from <input type="datetime-local">): already merchant-local
 *     wall clock, so take the date part verbatim. Parsing through Date() would
 *     reinterpret it as browser-local and can shift the day back.
 *   - with offset / Z: a real instant, so convert into the merchant timezone.
 */
function localDateOf(value, timeZone) {
  if (!value) return null;

  if (typeof value === 'string' && !HAS_TZ_INFO.test(value)) {
    const datePart = value.split('T')[0];
    return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : null;
  }

  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;

  if (!timeZone) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
  return new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit'
  }).format(d);
}

export function validateDateAgainstClosingPeriods(startStr, endStr, closingPeriods, timeZone) {
  if (!closingPeriods || closingPeriods.length === 0) return null;

  const startDate = localDateOf(startStr, timeZone);
  const endDate = localDateOf(endStr, timeZone);
  if (!startDate || !endDate) return null;

  for (const period of closingPeriods) {
    if (startDate <= period.end_date && endDate >= period.start_date) {
      return { type: 'closing', reason: period.reason || null };
    }
  }
  return null;
}

/**
 * TODO — flow_type is not passed yet, so this quotes from ALL pricing rules,
 * including `flow_type = 'immediate'` ones the reservation flow never offers.
 *
 * Agreed policy when this is implemented:
 *   - EXTEND: immediate rules MAY be used, but only as a fallback — if a
 *     non-immediate rule exists for the same duration, that one wins.
 *   - EDIT:   immediate rules are excluded outright.
 *
 * Note the schema has no 'extension' flow_type; the values are 'immediate',
 * 'reservation' and NULL (both). So "extension-only rule" has to be expressed as
 * "a rule that is not immediate-only" unless a new flow_type value is added —
 * worth settling with the v3 team before building it, since the column is shared.
 *
 * Also note this sends `new Date(startDate).toISOString()`, which reinterprets a
 * merchant-local `datetime-local` string as browser-local. Correct only while the
 * customer's timezone matches the merchant's. Same class of bug as the one
 * guarded against in validateDateAgainstOpeningHours above.
 */
export async function recalculatePrices(items, locationId, startDate) {
  const productTypeIds = [...new Set(items.map(i => i.product_type_id))];
  const priceMap = new Map();

  await Promise.all(
    productTypeIds.map(async (ptId) => {
      try {
        const sizeItem = items.find(i => i.product_type_id === ptId);
        const params = new URLSearchParams({
          productTypeId: ptId,
          locationId,
          startDate: new Date(startDate).toISOString()
        });
        if (sizeItem?.size) params.set('size', sizeItem.size);

        const res = await fetch(`${API_BASE}/calculate-price?${params}`);
        if (!res.ok) return;
        const data = await res.json();
        priceMap.set(ptId, data.pricingOptions || []);
      } catch {
        // keep original prices
      }
    })
  );

  return priceMap;
}

/**
 * Hours a pricing rule actually covers, or null when that can't be determined.
 *
 * `calculated_hours` from the calculate_rental_price RPC is the only trustworthy
 * value: for multi_day / week / until_closing rules it depends on the start date
 * and the location's closing time on the return day. It comes back NULL whenever
 * the RPC can't resolve a return moment — most commonly when a multi_day rule's
 * return day lands on a day the location is closed (`opening_hours.sunday: null`).
 *
 * It must NOT fall back to `duration_value`. That field is expressed in the
 * RULE's own unit, so a "9 Days" rule would be read as 9 hours and sort right
 * next to the "9 Hours" rule. That is exactly how a 9-hour edit came to be
 * priced at the 9-day rate — 260,00 € instead of 42,00 € (BK-2026-006126).
 *
 * A rule whose hours can't be resolved isn't offerable, so it is excluded from
 * matching entirely rather than coerced into a wrong number.
 */
function ruleHours(option) {
  const hours = parseFloat(option?.calculated_hours);
  return Number.isFinite(hours) && hours > 0 ? hours : null;
}

/**
 * Smallest pricing rule that covers the requested duration.
 *
 * @param {Array} pricingOptions - rows from /api/calculate-price
 * @param {string|Date} startDate
 * @param {string|Date} endDate
 * @param {string|null} [preferDurationType] - the booking's existing
 *   duration_type. Breaks ties in favour of keeping the customer on the same
 *   kind of rate instead of letting row order decide.
 * @returns {object|null} matching rule, or null when nothing covers the duration
 */
export function findBestPricingMatch(pricingOptions, startDate, endDate, preferDurationType = null) {
  if (!pricingOptions || pricingOptions.length === 0) return null;

  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
  const durationHours = (end - start) / (1000 * 60 * 60);

  const candidates = pricingOptions
    .map(option => ({ ...option, _hours: ruleHours(option) }))
    .filter(option => option._hours !== null)
    .sort((a, b) => {
      if (a._hours !== b._hours) return a._hours - b._hours;

      // Ties must not be settled by the incoming row order. Array.sort is stable,
      // so that order is the RPC's `display_order, price` — meaning a merchant
      // reordering rules in the admin UI would silently change what a customer
      // is charged. Prefer the rate type the booking already uses, then price.
      if (preferDurationType) {
        const aPreferred = a.duration_type === preferDurationType ? 0 : 1;
        const bPreferred = b.duration_type === preferDurationType ? 0 : 1;
        if (aPreferred !== bPreferred) return aPreferred - bPreferred;
      }
      return (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0);
    });

  // Smallest rule that meets-or-exceeds the requested duration. No fallback
  // to the longest rule when the duration exceeds every rule — the caller
  // must surface a "duration not supported" error and block save, otherwise
  // a multi-month rental would be silently priced at the longest rule's
  // (e.g. 4-hour) rate.
  return candidates.find(o => o._hours >= durationHours) || null;
}
